import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { Plus, Minus, Trash } from 'lucide-react';
import { useCart } from 'context/CartContext';
import { useModal } from 'context/ModalContext';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => `${theme.colors.black}85`};
  backdrop-filter: blur(8px);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  @media (max-width: 768px) {
    padding: 0;
    align-items: flex-end;
  }
`;

/* Desktop Container - unchanged behavior */
const Container = styled.div`
  width: 100%;
  max-width: 900px;
  max-height: 85vh;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  transform: scale(${({ $isOpen }) => ($isOpen ? 1 : 0.95)});
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition:
    transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 25px 60px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  position: relative;

  @media (max-width: 768px) {
    max-width: 100%;
    height: 75dvh;
    max-height: 75dvh;
    border-radius: 24px 24px 0 0;
    transform: translateY(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
    transition:
      transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
      opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.white};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.offWhite};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    color: ${({ theme }) => theme.colors.black};
  }
`;

/* Desktop layout - side by side */
const DesktopContent = styled.div`
  display: none;
  flex-direction: row;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
  height: 100%;

  @media (min-width: 769px) {
    display: flex;
  }
`;

/* Mobile layout - stacked with fixed sections */
const MobileContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;

  @media (min-width: 769px) {
    display: none;
  }
`;

/* Mobile: Fixed image section at top */
const MobileImageSection = styled.div`
  position: relative;
  width: 100%;
  height: 35%;
  min-height: 35%;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.offWhite};
  overflow: hidden;
`;

/* Mobile: Middle section that can expand over image */
const MobileMiddleSection = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: ${({ $isExpanded, $expandedTop }) =>
    $isExpanded ? $expandedTop : '35%'};
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ $isExpanded, $expandedTop }) =>
    $isExpanded && $expandedTop === '0%' ? '0' : '24px 24px 0 0'};
  display: flex;
  flex-direction: column;
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 5;
  overflow: hidden;
`;

/* Mobile: Scrollable text content area */
const MobileTextContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

/* Mobile: Fixed bottom actions */
const MobileActionsSection = styled.div`
  padding: 1rem 1.5rem;
  padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0));
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex-shrink: 0;
`;

const MobilePriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MobileButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

/* Desktop sections - keep original behavior */
const ImageSection = styled.div`
  flex-shrink: 0;
  width: 45%;
  height: auto;
  min-height: 500px;
  position: relative;
  background: ${({ theme }) => theme.colors.offWhite};
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.tan};
  color: ${({ theme }) => theme.colors.darkGray};
  font-size: 1rem;
`;

const DetailsSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScrollableContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
`;

const Title = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.black};
  margin: 0;
  line-height: 1.3;
  padding-right: 2rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    padding-right: 0;
  }
`;

const Description = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.darkGray};
  margin: 0;
  white-space: pre-wrap;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.5;
  }
`;

const MobileDescription = styled.p`
  font-size: 1.5rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.darkGray};
  margin: 0;
  white-space: pre-wrap;
  overflow: hidden;
  cursor: ${({ $needsExpansion }) => ($needsExpansion ? 'pointer' : 'default')};

  ${({ $isExpanded, $needsExpansion }) =>
    !$isExpanded &&
    $needsExpansion &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 7;
    -webkit-box-orient: vertical;
  `}
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.grey};
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.25rem 0;
  align-self: flex-start;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.darkGrey};
  }
`;

const Price = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.green};

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ActionsSection = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  background: ${({ theme }) => theme.colors.white};
  flex-shrink: 0;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const QuantityButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 9999px;
  border: 1.5px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => `${theme.colors.primary}14`};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: ${({ theme }) => theme.colors.primary};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.primary};
    border-color: transparent;
    color: ${({ theme }) => theme.colors.white};
    transform: translateY(-2px);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  min-width: 40px;
  text-align: center;
`;

const AddToCartButton = styled.button`
  flex: 1;
  min-height: 52px;
  padding: 0 1rem;
  border-radius: 14px;
  border: none;
  background-color: ${({ theme }) => theme.colors.green};
  color: ${({ theme }) => theme.colors.white};
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: ${({ theme }) => `0 4px 14px ${theme.colors.green}40`};

  &:hover {
    background-color: ${({ theme }) => theme.colors.darkGreen};
    transform: translateY(-2px);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ModifierButton = styled.button`
  width: 100%;
  height: 52px;
  padding: 0 1rem;
  border-radius: 14px;
  border: none;
  background-color: ${({ theme }) => theme.colors.green};
  color: ${({ theme }) => theme.colors.white};
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ theme }) => `0 4px 14px ${theme.colors.green}40`};

  &:hover {
    background-color: ${({ theme }) => theme.colors.darkGreen};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const InCartActions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex: 1;
`;

const UpdateButton = styled.button`
  flex: 1;
  height: 52px;
  padding: 0 1rem;
  border-radius: 14px;
  border: none;
  background-color: ${({ theme }) => theme.colors.green};
  color: ${({ theme }) => theme.colors.white};
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ theme }) => `0 4px 14px ${theme.colors.green}40`};

  &:hover {
    background-color: ${({ theme }) => theme.colors.darkGreen};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const RemoveButton = styled.button`
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: 14px;
  border: 1.5px solid ${({ theme }) => theme.colors.red};
  background: ${({ theme }) => `${theme.colors.red}14`};
  color: ${({ theme }) => theme.colors.red};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.red};
    border-color: transparent;
    color: ${({ theme }) => theme.colors.white};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => `0 8px 24px ${theme.colors.red}40`};
  }

  &:active {
    transform: translateY(0);
  }
`;

const CollapseImageButton = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.grey};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.25rem 0;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.darkGrey};
  }
`;

export const ItemDetailModal = ({
  isOpen,
  item,
  onClose,
  categoryId: _categoryId,
}) => {
  const {
    addToCart,
    updateCartItemQuantity,
    removeCartItem,
    findCartItemsByBaseId,
  } = useCart();
  const { openModal } = useModal();
  const [quantity, setQuantity] = React.useState(1);
  const [isDescriptionExpanded, setIsDescriptionExpanded] =
    React.useState(false);
  const [expandedTop, setExpandedTop] = React.useState('35%');
  const textContentRef = React.useRef(null);
  const actionsRef = React.useRef(null);

  // Cap description at 700 characters max, show "read more" after 400
  const MAX_DESCRIPTION_LENGTH = 700;
  const VISIBLE_DESCRIPTION_LENGTH = 400;
  const truncatedDescription = React.useMemo(() => {
    if (!item?.description) return '';
    if (item.description.length <= MAX_DESCRIPTION_LENGTH) {
      return item.description;
    }
    // Find a good break point (space) near the limit
    const truncated = item.description.slice(0, MAX_DESCRIPTION_LENGTH);
    const lastSpace = truncated.lastIndexOf(' ');
    return lastSpace > MAX_DESCRIPTION_LENGTH - 50
      ? truncated.slice(0, lastSpace) + '...'
      : truncated + '...';
  }, [item?.description]);

  const DESCRIPTION_THRESHOLD = VISIBLE_DESCRIPTION_LENGTH;
  const needsExpansion = truncatedDescription.length > DESCRIPTION_THRESHOLD;

  // Calculate how much to expand based on content height
  const calculateExpandedTop = React.useCallback(() => {
    if (!textContentRef.current || !actionsRef.current) return;

    const modalHeight = textContentRef.current.closest('[data-mobile-middle]')
      ?.parentElement?.offsetHeight;
    if (!modalHeight) return;

    // Get the full height needed for text content + actions
    const textContent = textContentRef.current;
    const actionsHeight = actionsRef.current.offsetHeight;

    // Temporarily remove line clamp to measure full height
    const description = textContent.querySelector('[data-description]');
    if (description) {
      description.style.display = 'block';
      description.style.webkitLineClamp = 'unset';
    }

    const fullContentHeight = textContent.scrollHeight + actionsHeight + 48; // 48px buffer for larger text

    // Reset styles
    if (description) {
      description.style.display = '';
      description.style.webkitLineClamp = '';
    }

    // Calculate the top position needed (as percentage)
    const neededTopPx = modalHeight - fullContentHeight;
    const minTop = 0; // Don't go above the modal
    const maxTop = modalHeight * 0.35; // Don't go below original position (35%)

    const clampedTop = Math.max(minTop, Math.min(maxTop, neededTopPx));
    const topPercentage = (clampedTop / modalHeight) * 100;

    setExpandedTop(`${Math.max(0, topPercentage - 5)}%`); // Extra 5% to ensure enough room
  }, []);

  // Recalculate on expand
  useEffect(() => {
    if (isDescriptionExpanded) {
      calculateExpandedTop();
    }
  }, [isDescriptionExpanded, calculateExpandedTop]);

  const cartItemsForThisItem = React.useMemo(() => {
    return findCartItemsByBaseId(item?.id || item?.name);
  }, [findCartItemsByBaseId, item?.id, item?.name]);

  const quantityInCart = React.useMemo(() => {
    return cartItemsForThisItem.reduce(
      (sum, cartItem) => sum + (cartItem.quantity || 0),
      0,
    );
  }, [cartItemsForThisItem]);

  const isInCart = quantityInCart > 0;

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setQuantity(1);
      setIsDescriptionExpanded(false);
      setExpandedTop('35%');
    }
  }, [isOpen]);

  const handleAddToCart = () => {
    if (item.modifierGroups && item.modifierGroups.length > 0) {
      openModal(item);
      onClose();
    } else {
      addToCart(item, quantity);
      onClose();
    }
  };

  const handleUpdateCart = () => {
    if (cartItemsForThisItem.length > 0) {
      const firstCartItem = cartItemsForThisItem[0];
      updateCartItemQuantity(
        firstCartItem.cartItemId,
        quantityInCart + quantity,
      );
    }
    onClose();
  };

  const handleRemoveFromCart = () => {
    cartItemsForThisItem.forEach((cartItem) => {
      removeCartItem(cartItem.cartItemId);
    });
    onClose();
  };

  const handleCustomize = () => {
    onClose();
    openModal(item);
  };

  if (!item) return null;

  const hasModifiers = item.modifierGroups && item.modifierGroups.length > 0;

  return createPortal(
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <Container $isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="Close modal">
          <X size={24} />
        </CloseButton>

        {/* Desktop Layout */}
        <DesktopContent>
          <ImageSection>
            {item.imageUrl ? (
              <Image src={item.imageUrl} alt={item.name} loading="lazy" />
            ) : (
              <ImagePlaceholder>No image available</ImagePlaceholder>
            )}
          </ImageSection>

          <DetailsSection>
            <ScrollableContent>
              <Title>{item.name}</Title>
              {truncatedDescription && (
                <Description>{truncatedDescription}</Description>
              )}
              <Price>${((item.price || 0) / 100).toFixed(2)}</Price>
            </ScrollableContent>

            <ActionsSection>
              {hasModifiers ? (
                <ModifierButton onClick={handleCustomize}>
                  Customize & Add to Cart
                </ModifierButton>
              ) : !isInCart ? (
                <>
                  <QuantityControl>
                    <QuantityButton
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      aria-label="Decrease quantity"
                    >
                      <Minus size={20} />
                    </QuantityButton>
                    <QuantityDisplay>{quantity}</QuantityDisplay>
                    <QuantityButton
                      onClick={() => setQuantity(quantity + 1)}
                      aria-label="Increase quantity"
                    >
                      <Plus size={20} />
                    </QuantityButton>
                  </QuantityControl>

                  <AddToCartButton onClick={handleAddToCart}>
                    <Plus size={20} />
                    Add {quantity > 1 ? `${quantity} ` : ''}to Cart
                  </AddToCartButton>
                </>
              ) : (
                <>
                  <QuantityControl>
                    <QuantityButton
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      aria-label="Decrease quantity"
                    >
                      <Minus size={20} />
                    </QuantityButton>
                    <QuantityDisplay>{quantity}</QuantityDisplay>
                    <QuantityButton
                      onClick={() => setQuantity(quantity + 1)}
                      aria-label="Increase quantity"
                    >
                      <Plus size={20} />
                    </QuantityButton>
                  </QuantityControl>

                  <InCartActions>
                    <UpdateButton onClick={handleUpdateCart}>
                      Update Cart ({quantityInCart} in cart)
                    </UpdateButton>
                    <RemoveButton
                      onClick={handleRemoveFromCart}
                      aria-label="Remove from cart"
                    >
                      <Trash size={20} />
                    </RemoveButton>
                  </InCartActions>
                </>
              )}
            </ActionsSection>
          </DetailsSection>
        </DesktopContent>

        {/* Mobile Layout */}
        <MobileContent>
          <MobileImageSection>
            {item.imageUrl ? (
              <Image src={item.imageUrl} alt={item.name} loading="lazy" />
            ) : (
              <ImagePlaceholder>No image available</ImagePlaceholder>
            )}
          </MobileImageSection>

          <MobileMiddleSection
            $isExpanded={isDescriptionExpanded}
            $expandedTop={expandedTop}
            data-mobile-middle
          >
            <MobileTextContent ref={textContentRef}>
              {isDescriptionExpanded && (
                <CollapseImageButton
                  onClick={() => setIsDescriptionExpanded(false)}
                >
                  ↓ Show image
                </CollapseImageButton>
              )}
              <Title>{item.name}</Title>
              {truncatedDescription && (
                <>
                  <MobileDescription
                    $isExpanded={isDescriptionExpanded}
                    $needsExpansion={needsExpansion}
                    data-description
                    onClick={() =>
                      needsExpansion &&
                      setIsDescriptionExpanded(!isDescriptionExpanded)
                    }
                  >
                    {truncatedDescription}
                  </MobileDescription>
                  {needsExpansion && !isDescriptionExpanded && (
                    <ExpandButton
                      onClick={() => setIsDescriptionExpanded(true)}
                    >
                      Read more
                    </ExpandButton>
                  )}
                </>
              )}
            </MobileTextContent>

            <MobileActionsSection ref={actionsRef}>
              <MobilePriceRow>
                <Price>${((item.price || 0) / 100).toFixed(2)}</Price>
              </MobilePriceRow>

              {hasModifiers ? (
                <ModifierButton onClick={handleCustomize}>
                  Customize & Add to Cart
                </ModifierButton>
              ) : !isInCart ? (
                <MobileButtonRow>
                  <QuantityControl>
                    <QuantityButton
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      aria-label="Decrease quantity"
                    >
                      <Minus size={18} />
                    </QuantityButton>
                    <QuantityDisplay>{quantity}</QuantityDisplay>
                    <QuantityButton
                      onClick={() => setQuantity(quantity + 1)}
                      aria-label="Increase quantity"
                    >
                      <Plus size={18} />
                    </QuantityButton>
                  </QuantityControl>

                  <AddToCartButton onClick={handleAddToCart}>
                    <Plus size={18} />
                    Add to Cart
                  </AddToCartButton>
                </MobileButtonRow>
              ) : (
                <>
                  <MobileButtonRow>
                    <QuantityControl>
                      <QuantityButton
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        disabled={quantity <= 1}
                        aria-label="Decrease quantity"
                      >
                        <Minus size={18} />
                      </QuantityButton>
                      <QuantityDisplay>{quantity}</QuantityDisplay>
                      <QuantityButton
                        onClick={() => setQuantity(quantity + 1)}
                        aria-label="Increase quantity"
                      >
                        <Plus size={18} />
                      </QuantityButton>
                    </QuantityControl>

                    <InCartActions>
                      <UpdateButton onClick={handleUpdateCart}>
                        Update ({quantityInCart})
                      </UpdateButton>
                      <RemoveButton
                        onClick={handleRemoveFromCart}
                        aria-label="Remove from cart"
                      >
                        <Trash size={18} />
                      </RemoveButton>
                    </InCartActions>
                  </MobileButtonRow>
                </>
              )}
            </MobileActionsSection>
          </MobileMiddleSection>
        </MobileContent>
      </Container>
    </Overlay>,
    document.body,
  );
};

ItemDetailModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    price: PropTypes.number,
    imageUrl: PropTypes.string,
    modifierGroups: PropTypes.array,
  }),
  onClose: PropTypes.func.isRequired,
  categoryId: PropTypes.string,
};

export default ItemDetailModal;
